import { useCallback, useEffect, useState } from 'react';

import {
  applyColorScheme, isDarkScheme, isThemeMode, resolveColorScheme, THEME_STORAGE_KEY,
  type ThemeMode,
} from './colorScheme';

export type { ThemeMode } from './colorScheme';

const readStored = (): ThemeMode => {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return isThemeMode(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
};

const prefersDark = () => window.matchMedia('(prefers-color-scheme: dark)').matches;

export function useThemeMode() {
  const [mode, setMode] = useState<ThemeMode>(readStored);
  const [systemDark, setSystemDark] = useState(prefersDark);

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setSystemDark(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  const scheme = resolveColorScheme(mode, systemDark);
  const isDark = isDarkScheme(scheme);

  useEffect(() => {
    applyColorScheme(scheme);
  }, [scheme]);

  const selectMode = useCallback((next: ThemeMode) => {
    setMode(next);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // storage unavailable
    }
  }, []);

  return { mode, isDark, selectMode };
}
